import {
  Box, LinearProgress, LinearProgressProps, Typography,
} from '@mui/material'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import TableMui from 'src/components/TableMui'
import { selectInventoryDetails } from '../../InventorySelector'
import { actions } from '../../InventoryState'
import { InventoryList, InventoryStatus, ItemStatus } from '../../InventoryTypes'
import ModalIncrement from '../ModalIncrement'
import ModalInventoryDetails from '../ModalInventoryDetails'
import * as S from './InventoryDetailList.styles'
import apis from '../../InventoryApi'
import { toast } from 'react-toastify'
import ModalCancelInventory from '../ModalCancelInventory'
import { formatDatePlusThree } from 'src/utils/formatDate'
import ConfirmOrRejectModal from 'src/features/base/components/ConfirmOrRejectModal'

interface Props {
    inventory: InventoryList
    setClickInventoryDetail: (value: boolean) => void
}

function LinearProgressWithLabel(props: LinearProgressProps & { value: number }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      <Box sx={{ width: '100%', mr: 1 }}>
        <LinearProgress variant='determinate' {...props} />
      </Box>
      <Box sx={{ minWidth: 35 }}>
        <Typography variant='body2' color='text.secondary'>{`${Math.round(
          props.value,
        )}%`}
        </Typography>
      </Box>
    </Box>
  )
}

export const InventoryDetailList = ({
  inventory, setClickInventoryDetail,
}: Props) => {
  const inventoryDetail = useSelector(selectInventoryDetails)
  const [openModalIncrement, setOpenModalIncrement] = useState(false)
  const [openModalInventoryDetails, setOpenModalInventoryDetails] = useState(false)
  const [progress, setProgress] = useState(0)
  const [concluded, setConcluded] = useState(0)
  const [clickFinish, setClickFinish] = useState(false)
  const [listProductsError, setListProductsError] = useState<string[]>([])
  const [openModalCancel, setOpenModalCancel] = useState(false)
  const [idForCancel, setIdForCancel] = useState()
  const [openModalResetApprovalAdjustment, setOpenModalResetApprovalAdjustment] = useState(false)
  const productIdToRemoveCurrentApproval = useRef<number>(0)
  const [selectedProduct, setSelectedProduct] = useState<{inventoryId: number, productCode: string} | null>(null)

  const numberOfProducts = inventoryDetail?.products?.length

  const dispatch = useDispatch()

  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((concluded / numberOfProducts) * 100)
    }, 200)
    return () => {
      clearInterval(timer)
    }
  }, [numberOfProducts, concluded])

  useEffect(() => {
    dispatch(actions.getInventoryDetail({ id: inventory.id }))
  }, [dispatch, inventory.id])

  useEffect(() => {
    if (progress === 100 && listProductsError.length > 0) {
      toast.warn('Confira na tela os produtos que não puderam ser finalizados e tente novamente!')
      setClickFinish(false)
    }
    if (progress === 100 && listProductsError.length === 0) {
      toast.success('Inventário finalizado com sucesso!')
      setClickFinish(false)
      setClickInventoryDetail(false)
      dispatch(actions.setInventoryStatus({ id: inventory.id, currentStatus: InventoryStatus.FINISHED }))
      dispatch(actions.clearState())
    }
  }, [progress, dispatch, setClickInventoryDetail, listProductsError.length, inventory.id])

  const onClickRefresh = () => {
    dispatch(actions.getInventoryDetail({ id: inventory.id }))
  }

  const onClickApprove = (id: number, status: boolean) => {
    dispatch(actions.approveAdjustment({ idProductInventory: id, approvalStatus: status }))
  }

  const onClickReject = (id: number, status: boolean) => {
    dispatch(actions.rejectAdjustment({ idProductInventory: id, approvalStatus: status }))
  }

  const onClickIncrement = () => {
    setOpenModalIncrement(true)
  }

  const onClickDetails = (inventoryId: number, productCode: string) => {
    setSelectedProduct({ inventoryId, productCode })
    setOpenModalInventoryDetails(true)
  }

  const onRowClick = (row: any, event?: React.MouseEvent) => {
    if (event) {
      const target = event.target as HTMLElement
      if (
        target.closest('button')
      ) {
        return
      }
    }
    onClickDetails(inventory.id, row.productCode)
  }

  const callApi = async (product) => {
    const response = await apis.finishInventory({ id: product.id })

    if (!response.isOk) {
      setListProductsError((prevState) => [...prevState, product.productCode])
    }

    setConcluded((prevState) => prevState + 1)
  }

  const onClickFinishInventory = () => {
    setListProductsError([])
    setProgress(0)
    setConcluded(0)
    setClickFinish(true)
    inventoryDetail?.products?.forEach((p) => {
      callApi(p)
    })
  }

  const onClickResetApprovalAdjustment = (id: number) => {
    productIdToRemoveCurrentApproval.current = id
    setOpenModalResetApprovalAdjustment(true)
  }

  const onConfirmResetApprovalAdjustment = (id: number) => {
    dispatch(actions.resetAdjustmentApproval({ idProductInventory: id }))
    setOpenModalResetApprovalAdjustment(false)
  }

  const tableRows = inventoryDetail?.products?.map((p) => ({
    ...p,
    quantityFirstCount: p.firstCountQuantity === 0 ? '0' : p.firstCountQuantity,
    damageFirstCount: p.firstCountDamage === 0 ? '0' : p.firstCountDamage,
    quantitySecondCount:
      p.secondCountQuantity === 0 ? '0' : p.secondCountQuantity,
    damageSecondCount: p.secondCountDamage === 0 ? '0' : p.secondCountDamage,
    quantityThirdCount: p.thirdCountQuantity === 0 ? '0' : p.thirdCountQuantity,
    damageThirdCount: p.thirdCountDamage === 0 ? '0' : p.thirdCountDamage,
    finalCount: p.finalCount === 0 ? '0' : p.finalCount,
    finalAdjustment: p.finalAdjustment === 0 ? '0' : p.finalAdjustment,
    increment: p.increment === 0 ? '0' : p.increment,
    ruleOne:
      p.ruleOne === true
      && (p.status === ItemStatus.ADJUSTMENT_ANALYSIS
        || p.status === ItemStatus.FINISHED) ? (
          <S.TrueIcon />
        ) : p.ruleOne === false
        && (p.status === ItemStatus.ADJUSTMENT_ANALYSIS
          || p.status === ItemStatus.FINISHED) ? (
            <S.FalseIcon />
          ) : (
            ''
          ),
    ruleTwo:
      p.ruleTwo === true
      && (p.status === ItemStatus.ADJUSTMENT_ANALYSIS
        || p.status === ItemStatus.FINISHED) ? (
          <S.TrueIcon />
        ) : p.ruleTwo === false
        && (p.status === ItemStatus.ADJUSTMENT_ANALYSIS
          || p.status === ItemStatus.FINISHED) ? (
            <S.FalseIcon />
          ) : (
            ''
          ),
    ruleThree:
      p.ruleThree === true
      && (p.status === ItemStatus.ADJUSTMENT_ANALYSIS
        || p.status === ItemStatus.FINISHED) ? (
          <S.TrueIcon />
        ) : p.ruleThree === false
        && (p.status === ItemStatus.ADJUSTMENT_ANALYSIS
          || p.status === ItemStatus.FINISHED) ? (
            <S.FalseIcon />
          ) : (
            ''
          ),
    actions:
      p.status === ItemStatus.ADJUSTMENT_ANALYSIS
      && p.adjustmentApproval === null ? (
        <S.Options>
          <S.ApprovedIcon
            onClick={(e) => { e.stopPropagation(); onClickApprove(p.id, true) }}
            data-testid='approve-adjustment'
          />
          <S.RejectedIcon
            onClick={(e) => { e.stopPropagation(); onClickReject(p.id, false) }}
            data-testid='reject-adjustment'
          />
        </S.Options>
        ) : p.adjustmentApproval === true ? (
          <S.CellTolltip title='Clique para excluir decisão' arrow>
            <S.ApprovedIcon
              onClick={(e) => { e.stopPropagation(); onClickResetApprovalAdjustment(p.id) }}
              data-testid='reset-adjustment-by-icon-approve'
            />
          </S.CellTolltip>
        ) : p.adjustmentApproval === false ? (
          <S.CellTolltip title='Clique para excluir decisão' arrow>
            <S.RejectedIcon
              onClick={(e) => { e.stopPropagation(); onClickResetApprovalAdjustment(p.id) }}
              data-testid='reset-adjustment-by-icon-reject'
            />
          </S.CellTolltip>
        ) : (
          <S.Options>
            <S.ApprovedIconDisabled />
            <S.RejectedIconDisabled />
          </S.Options>
        ),
  }))

  const onClickCancelInventory = (id) => {
    setIdForCancel(id)
    setOpenModalCancel(true)
  }

  return (
    <S.Wrapper>
      <S.ContainerButton>
        <S.CustomButton
          title='Incrementar estoque'
          bgColor='blue'
          hoverColor='darkBlueHover'
          letterColor='white'
          onClick={onClickIncrement}
          data-testid='increment-inventory'
          disabled={inventoryDetail.inventoryStatus === 'Cancelado' || inventoryDetail.inventoryStatus === 'Finalizado'}
        />
        <S.CustomButtonRefresh
          title={<S.RefreshButton/>}
          onClick={onClickRefresh}
        />
      </S.ContainerButton>
      <S.InventoryInfo>
        <span><strong>Nome do inventário:</strong> {inventoryDetail.inventoryName} </span>
        <span><strong>Tipo:</strong> {inventoryDetail.countType} </span>
        <span><strong>Data de criação:</strong> {formatDatePlusThree(inventoryDetail.createdAt)} </span>
        <span><strong>Criado por:</strong> {inventoryDetail.creatorUserName}</span>
      </S.InventoryInfo>
      <TableMui
        columns={[
          { id: 'productCode', label: 'Cód. produto', width: 1 },
          { id: 'productDescription', label: 'Descrição' },
          { id: 'status', label: 'Status' },
          {
            id: 'quantityFirstCount', label: 'Primeira Qtd.', backgroundColor: '#3079d1', width: 1,
          },
          {
            id: 'damageFirstCount', label: 'Primeira Avarias', backgroundColor: '#3079d1', width: 1,
          },
          { id: 'quantitySecondCount', label: 'Segunda Qtd.', width: 1 },
          { id: 'damageSecondCount', label: 'Segunda Avarias', width: 1 },
          {
            id: 'quantityThirdCount', label: 'Terceira Qtd.', backgroundColor: '#3079d1', width: 1,
          },
          {
            id: 'damageThirdCount', label: 'Terceira Avarias', backgroundColor: '#3079d1', width: 1,
          },
          { id: 'finalCount', label: 'Contagem Final', width: 1 },
          { id: 'increment', label: 'Incremento', width: 1 },
          {
            id: 'ruleOne',
            label: (
              <S.CellTolltip title='O item varia menos de 10 unidades' arrow>
                <span>{'<10'}</span>
              </S.CellTolltip>
            ),
            width: 1,
          },
          {
            id: 'ruleTwo',
            label: (
              <S.CellTolltip title='Valor do ajuste é menor que R$50,00 utilizando o custo de estoque' arrow>
                <S.MoneyIcon />
              </S.CellTolltip>
            ),
            width: 1,
          },
          {
            id: 'ruleThree',
            label: (
              <S.CellTolltip title='A variação do ajuste é inferior a 20% do total do estoque' arrow>
                <S.PercentIcon />
              </S.CellTolltip>
            ),
            width: 1,
          },
          { id: 'finalAdjustment', label: 'Ajuste final', width: 1 },
          { id: 'actions', label: 'Ações', width: 1 },
        ]}
        emptyLabel='Nenhum detalhe de inventário encontrado'
        rows={tableRows}
        enablePagination
        rowsPerPageLimit={10}
        onRowClick={onRowClick}
      />
      <S.ContainerButton>
        <S.CustomButton
          title='Voltar'
          bgColor='blue'
          hoverColor='darkBlueHover'
          letterColor='white'
          onClick={() => setClickInventoryDetail(false)}
        />
        <S.CustomButton
          title='Cancelar Inventário'
          bgColor='lightRed'
          hoverColor='cancelHover'
          letterColor='white'
          onClick={() => onClickCancelInventory(inventoryDetail.id)}
          disabled={inventoryDetail.inventoryStatus === 'Cancelado' || inventoryDetail.inventoryStatus === 'Finalizado'}
        />
        <S.CustomButton
          title='Finalizar inventário'
          bgColor='confirm'
          hoverColor='confirmHover'
          letterColor='white'
          disabled={inventoryDetail?.products?.some((p) => p.adjustmentApproval === null) || clickFinish || inventoryDetail.inventoryStatus === 'Cancelado' || inventoryDetail.inventoryStatus === 'Finalizado'}
          onClick={onClickFinishInventory}
          data-testid='finish-inventory-button'
        />
      </S.ContainerButton>
      <S.ContainerProgress>
        {clickFinish && (
          <Box sx={{ width: '50%' }}>
            <LinearProgressWithLabel value={progress} />
          </Box>
        )}
        {listProductsError.length > 0 && (
          <>
            <h6>Códigos dos produtos não finalizados:</h6>
            <p>{listProductsError.map((p) => <S.ContainerProductsError> {p}</S.ContainerProductsError>)}
            </p>
          </>
        )}
      </S.ContainerProgress>
      <ModalIncrement
        open={openModalIncrement}
        setOpen={setOpenModalIncrement}
        id={inventory.id}
      />
      <ModalInventoryDetails
        open={openModalInventoryDetails}
        setOpen={setOpenModalInventoryDetails}
        id={inventory.id}
        productCode={selectedProduct?.productCode}
      />
      <ModalCancelInventory
        open={openModalCancel}
        setOpen={setOpenModalCancel}
        idForCancel={idForCancel}
      />
      <ConfirmOrRejectModal
        show={openModalResetApprovalAdjustment}
        title='Excluir decisão de ajuste do produto'
        description='Você tem certeza que deseja excluir a decisão de ajuste deste produto?'
        onConfirm={() => onConfirmResetApprovalAdjustment(productIdToRemoveCurrentApproval.current)}
        onReject={() => setOpenModalResetApprovalAdjustment(false)}
        onClose={() => setOpenModalResetApprovalAdjustment(false)}
        dataTestId='modal-reset-approval-adjustment'
      />
    </S.Wrapper>
  )
}
